import { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './AzamraHomes.css';

const villaTypes = [
  {
    label: 'Type A — Oceanfront Villas',
    beds: '5 Bedrooms',
    floors: '2 Floors',
    lots: 'Lots 1–18 (18 Villas)',
    size: '700m² to 1,200m² per lot',
    tags: ['Private Pool', '4 Parking Spaces', 'Direct Beach Access'],
  },
  {
    label: 'Type B — Second Line Villas',
    beds: '4 Bedrooms',
    floors: '2 Floors',
    lots: 'Lots 19–53 (35 Villas)',
    size: '500m² to 700m² per lot',
    tags: ['2 Parking Spaces', 'Beach Club Access', 'Sea Views'],
  },
  {
    label: 'Type C — Interior Villas',
    beds: '3 Bedrooms',
    floors: 'Ground Floor',
    lots: 'Lots 54–89 (36 Villas)',
    size: '350m² to 450m² per lot',
    tags: ['2 Parking Spaces', 'Landscaped Gardens', 'Community Access'],
  },
];

const amenities = [
  {
    title: 'Beach Club & Infinity Pool',
    desc: '2,500m² beach club with infinity pool, sun loungers, cabanas, and direct access to the ocean.',
    tags: ['Infinity Pool', 'Cabanas', 'Beach Bar'],
  },
  {
    title: 'Restaurant, Bar & Lounge',
    desc: 'Oceanfront dining terrace with full restaurant, cocktail bar, and lounge — open to residents and guests.',
    tags: ['Terrace Dining', 'Cocktails', 'Lounge'],
  },
  {
    title: 'Conference & Events',
    desc: 'State-of-the-art conference and reception facilities for up to 500 guests — weddings, corporate events, and receptions.',
    tags: ['500 Guests', 'Weddings', 'Corporate'],
  },
  {
    title: 'Security & Infrastructure',
    desc: '24/7 gated security, wide landscaped roads, visitor parking, children\'s play area, and a fully managed Club House.',
    tags: ['24/7 Security', 'Club House', 'Play Area'],
  },
];

const AzamraHomes = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    cashBuyer: '',
    mortgageBuyer: '',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Connect to business email once address is provided by client
    setSubmitted(true);
  };

  const scrollToForm = () => {
    document.getElementById('register-interest').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Oceanfront Residences — Azamra Homes</title>
        <meta name="description" content="Oceanfront Residences — 89 luxury villas with direct beach access, Beach Club, and world-class amenities on the Atlantic coast of Cameroon." />
        <meta property="og:title" content="Oceanfront Residences — Azamra Homes" />
        <meta property="og:url" content="https://www.azamra.com/marketplace/azamra-homes" />
        <link rel="canonical" href="https://www.azamra.com/marketplace/azamra-homes" />
      </Helmet>
      <Header />

      {/* ── SECTION 1: HERO ── */}
      <section className="ah-hero-section" style={{ backgroundImage: `url('/assets/images/kribi-villa.webp')` }}>
        <div className="ah-hero-overlay" />
        <Container>
          <Row className="align-items-center ah-hero-row">
            <Col lg={7} md={12} className="ah-hero-left">
              <span className="ah-hero-tag">Marketplace · Azamra Homes</span>
              <h1 className="ah-hero-title">Oceanfront Residences</h1>
              <p className="ah-hero-subtitle">
                Nature, Luxury & Elegance — A world-class gated residential community with direct ocean access on the Atlantic coast of Cameroon.
              </p>
              <div className="ah-hero-actions">
                <button className="ah-hero-btn" onClick={scrollToForm}>
                  Register Your Interest
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── SECTION 2: ABOUT THE PROJECT ── */}
      <section className="ah-about-section">
        <Container>
          <Row className="align-items-center ah-about-row">

            <Col lg={6} md={12} className="ah-about-left">
              <h2 className="ah-about-title">A Place of Exception Between Ocean, Nature and Luxury</h2>
              <p className="ah-about-para">
                Oceanfront Residences is a premium gated community offering 89 high-standing villas with private parking, direct beach access, and world-class shared amenities on the Atlantic coast of Cameroon.
              </p>
              <p className="ah-about-para">
                Situated in one of Africa's most sought-after coastal destinations — the development spans 8.9 hectares of prime oceanfront land, blending modern tropical architecture with lush landscaped surroundings.
              </p>
              <p className="ah-about-para ah-about-para-emphasis">
                Whether as a primary residence, holiday home, or investment property, our Oceanfront Residences offers unmatched value on Africa's Atlantic coastline.
              </p>
              <div className="ah-about-stats">
                <div className="ah-stat">
                  <span className="ah-stat-number">89</span>
                  <span className="ah-stat-label">Luxury Villas</span>
                </div>
                <div className="ah-stat">
                  <span className="ah-stat-number">8.9ha</span>
                  <span className="ah-stat-label">Total Land</span>
                </div>
                <div className="ah-stat">
                  <span className="ah-stat-number">3</span>
                  <span className="ah-stat-label">Villa Types</span>
                </div>
              </div>
            </Col>

            <Col lg={6} md={12} className="ah-about-right">
              <div className="ah-about-img-wrap">
                <img
                  src="/assets/images/oceanfront-masterplan.webp"
                  alt="Oceanfront Residences — masterplan"
                  className="ah-about-img"
                />
              </div>
            </Col>

          </Row>
          <div className="ah-section-divider" />
        </Container>
      </section>

      {/* ── SECTION 3: VILLA TYPES ── */}
      <section className="ah-villas-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12} className="text-center">
              <h2 className="ah-features-title">Choose Your Villa</h2>
              <p className="ah-features-desc">
                Three distinct villa types designed to suit every lifestyle — from direct oceanfront living to landscaped garden residences.
              </p>
            </Col>
          </Row>
          <Row className="ah-features-row">
            {villaTypes.map((villa) => (
              <Col lg={4} md={12} xs={12} className="ah-feature-col" key={villa.label}>
                <div className="ah-villa-card">
                  <h4 className="ah-villa-title">{villa.label}</h4>
                  <div className="ah-villa-details">
                    <div className="ah-villa-detail-row">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{villa.beds}</span>
                    </div>
                    <div className="ah-villa-detail-row">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{villa.floors}</span>
                    </div>
                    <div className="ah-villa-detail-row">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{villa.lots}</span>
                    </div>
                    <div className="ah-villa-detail-row">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{villa.size}</span>
                    </div>
                  </div>
                  <div className="ah-feature-tags">
                    {villa.tags.map((tag) => (
                      <span className="ah-feature-tag" key={tag}>
                        <svg width="10" height="10" viewBox="0 0 10 10" className="ah-tag-check">
                          <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#0038B6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </svg>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <button className="ah-villa-cta" onClick={scrollToForm}>Register Interest</button>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── SECTION 4: AMENITIES ── */}
      <section className="ah-features-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12} className="text-center">
              <h2 className="ah-features-title">World-Class Amenities</h2>
              <p className="ah-features-desc">
                Every detail of Oceanfront Residences is designed to deliver an exceptional lifestyle — from the beach to the boardroom.
              </p>
            </Col>
          </Row>
          <Row className="ah-features-row">
            {amenities.map((item) => (
              <Col lg={3} md={6} sm={6} xs={12} className="ah-feature-col" key={item.title}>
                <div className="ah-feature-card">
                  <h4 className="ah-feature-title">{item.title}</h4>
                  <p className="ah-feature-desc">{item.desc}</p>
                  <div className="ah-feature-tags">
                    {item.tags.map((tag) => (
                      <span className="ah-feature-tag" key={tag}>
                        <svg width="10" height="10" viewBox="0 0 10 10" className="ah-tag-check">
                          <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#0038B6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </svg>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── SECTION 5: MID CTA BANNER ── */}
      <section className="ah-mid-cta-section">
        <Container>
          <div className="ah-mid-cta-banner" style={{ backgroundImage: `url('/assets/images/${isMobile ? 'cta1.webp' : 'cta.webp'}')` }}>
            <h2 className="ah-mid-cta-title">
              Your Oceanfront Home in Kribi Awaits.
            </h2>
            <p className="ah-mid-cta-desc">
              89 luxury villas. Direct beach access. World-class amenities. Register your interest today and be among the first to secure your villa.
            </p>
            <button className="ah-mid-cta-btn" onClick={scrollToForm}>
              Register Your Interest
            </button>
          </div>
        </Container>
      </section>

      {/* ── SECTION 6: REGISTER YOUR INTEREST FORM ── */}
      <section className="ah-form-section" id="register-interest">
        <Container>
          <Row className="ah-form-row">

            <Col lg={6} md={12} xs={12} className="ah-form-col">
              <div className="ah-form-wrap">
                <h2 className="ah-form-title">Register Your Interest</h2>

                {submitted ? (
                  <div className="ah-form-success">
                    <p className="ah-form-success-text">Thank you! We have received your details and our team will be in touch shortly with more information about Oceanfront Residences.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="ah-form">
                    <div className="ah-field-col">
                      <input
                        type="text"
                        name="fullName"
                        className="ah-input"
                        placeholder="Full Name"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="ah-field-col">
                      <input
                        type="email"
                        name="email"
                        className="ah-input"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="ah-field-col">
                      <input
                        type="tel"
                        name="phone"
                        className="ah-input"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="ah-field-col">
                      <input
                        type="text"
                        name="country"
                        className="ah-input"
                        placeholder="Country of Residence"
                        value={formData.country}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="ah-field-col">
                      <p className="ah-radio-question">Are you a Cash Buyer?</p>
                      <div className="ah-radio-group">
                        <label className="ah-radio-label">
                          <input type="radio" name="cashBuyer" value="yes" checked={formData.cashBuyer === 'yes'} onChange={handleChange} required />
                          Yes
                        </label>
                        <label className="ah-radio-label">
                          <input type="radio" name="cashBuyer" value="no" checked={formData.cashBuyer === 'no'} onChange={handleChange} />
                          No
                        </label>
                      </div>
                    </div>
                    {formData.cashBuyer === 'no' && (
                      <div className="ah-field-col">
                        <p className="ah-radio-question">Are you a Mortgage Buyer?</p>
                        <div className="ah-radio-group">
                          <label className="ah-radio-label">
                            <input type="radio" name="mortgageBuyer" value="yes" checked={formData.mortgageBuyer === 'yes'} onChange={handleChange} required />
                            Yes
                          </label>
                          <label className="ah-radio-label">
                            <input type="radio" name="mortgageBuyer" value="no" checked={formData.mortgageBuyer === 'no'} onChange={handleChange} />
                            No
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="ah-field-col">
                      <button type="submit" className="ah-submit-btn">Submit</button>
                    </div>
                  </form>
                )}
              </div>
            </Col>

            <Col lg={6} md={12} xs={12} className="ah-form-info-col">
              <div className="ah-form-info-wrap">
                <h2 className="ah-form-info-title">Why Register?</h2>
                <div className="ah-form-info-items">
                  <div className="ah-form-info-item">
                    <div className="ah-form-info-check">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <p className="ah-form-info-heading">Priority Villa Selection</p>
                      <p className="ah-form-info-text">Be first to choose from all 89 villas — oceanfront, second line, and interior lots.</p>
                    </div>
                  </div>
                  <div className="ah-form-info-item">
                    <div className="ah-form-info-check">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <p className="ah-form-info-heading">Tailored Buying Options</p>
                      <p className="ah-form-info-text">Our team will guide you through cash purchase and mortgage financing options.</p>
                    </div>
                  </div>
                  <div className="ah-form-info-item">
                    <div className="ah-form-info-check">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <p className="ah-form-info-heading">No Commitment</p>
                      <p className="ah-form-info-text">Registering your interest is completely free and carries no obligation.</p>
                    </div>
                  </div>
                  <div className="ah-form-info-item">
                    <div className="ah-form-info-check">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <p className="ah-form-info-heading">Expert Guidance</p>
                      <p className="ah-form-info-text">Dedicated support from the Azamra Homes team from enquiry through to completion.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

          </Row>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default AzamraHomes;
